/**
 * Facilities SoT — backed by the real `/v2/submissions/{id}/locations`
 * endpoint (orion-backend PR #261).
 *
 * Schedule → Facility Schedule and UW Information → Schedule of
 * Location both consume this through `useFacilities`. The FE-facing
 * `Facility` shape is kept stable; this module adapts it to/from the
 * backend `SubmissionLocationWire`.
 *
 * `providerNumber`, `acquired` and `operator` are sent to the backend
 * forward-compatibly (as `provider_number` / `acquired_date` /
 * `operator`) but the dev backend has no columns for them yet, so they
 * round-trip as blank until the SubmissionLocation migration ships.
 * `maturityCredit` is whole-percent in the FE (the dropdown values are
 * "43".."100"), but the backend stores it as a fraction
 * (`maturity_credit` = 1.0 means 100%, same unit as `sir_credit` and
 * the credit-factors service — Henry, OR-1511), so it's converted ×100
 * on read and ÷100 on write.
 * Truly FE-only (never sent): `censusMemoryCare`.
 * Derived values (occupancy, rate, premiums) are computed client-side and
 * sent back on a Schedule save through `LocationComputedFields`, so the
 * backend's premium columns hold what the Schedule last showed. The rate
 * also travels the other way: `actuarial_rate` is read into the facility
 * so an imported pricing model keeps the rate it came with.
 *
 * Endpoint shape (RESTful, scoped under each submission):
 *   GET    /v2/submissions/:submissionId/locations
 *   POST   /v2/submissions/:submissionId/locations      body: SubmissionLocationCreate
 *   PATCH  /v2/submissions/:submissionId/locations/:id   body: SubmissionLocationUpdate
 *   DELETE /v2/submissions/:submissionId/locations/:id
 */
import type { Facility, FacilityWritePayload } from "../../types/facility";
import type { SubmissionLocationWire } from "../../types/submission";
import {
    emptyToNull,
    formDateToIso,
    inputToNumber,
    isoDateToForm,
    numberToInput,
    strOrEmpty,
} from "../adapters/submission";
import type { LocationWritePayload } from "../adapters/submission";
import { createLocation, deleteLocation, listLocations, patchLocation } from "./submissions";

/** Backend `urbanicity` is a free string; the FE only knows two values. */
function normalizeGeo(value: string | null): Facility["geo"] {
    return value === "Urban" || value === "Rural" ? value : "";
}

/**
 * Backend `maturity_credit` → FE whole-percent number ("100" for 1.0).
 *
 * The canonical unit is a fraction (1.0 = 100%, Henry, OR-1511). Rows the
 * previous FE saved hold a whole percent instead (80 meaning 80%), and a
 * blanket ×100 would read those as 8000%. A credit can't exceed 100%, so
 * any value above 1 is unambiguously legacy whole-percent and is passed
 * through as is; the next save writes it back as a fraction. TEMPORARY:
 * remove once the backend normalizes the stored values (÷100 where > 1).
 * `toFixed` strips float artifacts (0.43 × 100 = 43.0000…1) so the value
 * matches a dropdown preset id ("43") instead of dropping to custom. Four
 * places, not two: factors carry up to three decimals of a percent
 * (99.215%, OR-1790), and two would round them on every reload.
 */
export function maturityCreditToPercent(value: number | null | undefined): number | null {
    if (value == null) return null;
    if (value > 1) return value; // legacy whole-percent row
    return Number((value * 100).toFixed(4));
}

/** Backend location row → FE-facing `Facility`. Fields with no backend
 *  column come back blank (see module header). */
export function locationToFacility(wire: SubmissionLocationWire): Facility {
    return {
        id: wire.id,
        submissionId: wire.submission_id,
        // Global catalog id this location links to — the join handle for
        // Surveys / Operational staffing / Occupancies. Read-only.
        catalogFacilityId: strOrEmpty(wire.facility_id),
        name: strOrEmpty(wire.location_name),
        // Provider Number = the facility's CMS/CCN number. The locations
        // endpoint returns it as `cms_number`; fall back to the legacy
        // `provider_number` if a response predates that field.
        providerNumber: strOrEmpty(wire.cms_number ?? wire.provider_number),
        acquired: isoDateToForm(wire.acquired_date ?? null),
        operator: strOrEmpty(wire.operator_name),
        address: strOrEmpty(wire.address),
        city: strOrEmpty(wire.city),
        state: strOrEmpty(wire.state),
        zip: strOrEmpty(wire.zip_code),
        county: strOrEmpty(wire.county),
        geo: normalizeGeo(wire.urbanicity),
        // The rate the pricing-model import stored on this location. Seeds
        // the Schedule's Rate column so an imported submission prices with
        // the workbook's own rate instead of a fresh rates-table lookup.
        actuarialRate: numberToInput(wire.actuarial_rate),
        plRetroactiveDate: isoDateToForm(wire.pl_retroactive_date),
        glRetroactiveDate: isoDateToForm(wire.gl_retroactive_date),
        retention: numberToInput(wire.sir),
        // The SIR credit the pricing-model import stored on this location,
        // for the same reason as `actuarialRate`: the lookup that would
        // otherwise answer is keyed on data the sheet does not round-trip.
        sirCredit: numberToInput(wire.sir_credit),
        plLimit: numberToInput(wire.pl_limit),
        glLimit: numberToInput(wire.gl_limit),
        censusSkilled: numberToInput(wire.skilled_beds),
        censusMemoryCare: "", // FE-only — backend has no in-use MC count
        censusAssisted: numberToInput(wire.assisted_beds),
        censusIndependent: numberToInput(wire.independent_beds),
        censusAddlExposure: numberToInput(wire.additional_beds),
        maturityCredit: numberToInput(maturityCreditToPercent(wire.maturity_credit)),
        licensedSnf: numberToInput(wire.skilled_beds_licensed),
        licensedMc: numberToInput(wire.memory_care_beds_licensed),
        licensedAlf: numberToInput(wire.assisted_beds_licensed),
        licensedIlf: numberToInput(wire.independent_beds_licensed),
        duplicateSubmissionId: wire.duplicate_submission_id ?? "",
        // Archived flag (OR-1431). Defaults to false so a response that
        // predates the column reads as "not archived" rather than undefined.
        isExcluded: wire.is_excluded === true,
        createdAt: wire.created_at,
        updatedAt: wire.updated_at,
    };
}

/**
 * FE-computed pricing values for a single location (Henry, jun-23).
 * These are derived by `domain/pricing/computeFacilityRow` and sent to
 * the backend on save so it can run its downstream quote calculations.
 * The FE owns the math; the backend just persists + consumes the result.
 */
export interface LocationComputedFields {
    equivalency: number | null;
    actuarial_rate: number | null;
    unadjusted_premium: number | null;
    maturity_adjusted_premium: number | null;
    sir_credit: number | null;
    sir_adjusted_premium: number | null;
}

/** FE write payload → backend location body. Only keys present in the
 *  input are emitted, so a single-cell PATCH never wipes siblings.
 *  FE-only fields are dropped (see module header). When `computed` is
 *  supplied (Schedule save) the six derived pricing values are appended
 *  so the backend can recompute the quote downstream. */
export function facilityToLocationWrite(
    payload: FacilityWritePayload,
    computed?: LocationComputedFields,
): LocationWritePayload {
    const out: LocationWritePayload = {};
    if (payload.name !== undefined) out.location_name = emptyToNull(payload.name);
    // Link to the global catalog facility. Only emitted when non-empty so a
    // normal Schedule PATCH (which never sets catalogFacilityId) can't null
    // out an existing link. Used when other sections (e.g. Regulatory Add
    // Row) create a location to attach a survey/occupancy to.
    if (payload.catalogFacilityId) out.facility_id = payload.catalogFacilityId;
    // Provider Number = the facility's CMS/CCN id — real backend column
    // (`cms_number`), so it persists on save.
    if (payload.providerNumber !== undefined) out.cms_number = emptyToNull(payload.providerNumber);
    // Acquired / Operator — sent forward-compatibly (no backend column yet;
    // ignored until the SubmissionLocation migration adds them).
    if (payload.acquired !== undefined) out.acquired_date = formDateToIso(payload.acquired);
    if (payload.operator !== undefined) out.operator_name = emptyToNull(payload.operator);
    if (payload.address !== undefined) out.address = emptyToNull(payload.address);
    if (payload.city !== undefined) out.city = emptyToNull(payload.city);
    if (payload.state !== undefined) out.state = emptyToNull(payload.state);
    if (payload.zip !== undefined) out.zip_code = emptyToNull(payload.zip);
    if (payload.county !== undefined) out.county = emptyToNull(payload.county);
    if (payload.geo !== undefined) out.urbanicity = emptyToNull(payload.geo);
    if (payload.plRetroactiveDate !== undefined) out.pl_retroactive_date = formDateToIso(payload.plRetroactiveDate);
    if (payload.glRetroactiveDate !== undefined) out.gl_retroactive_date = formDateToIso(payload.glRetroactiveDate);
    if (payload.retention !== undefined) out.sir = inputToNumber(payload.retention);
    if (payload.plLimit !== undefined) out.pl_limit = inputToNumber(payload.plLimit);
    if (payload.glLimit !== undefined) out.gl_limit = inputToNumber(payload.glLimit);
    if (payload.censusSkilled !== undefined) out.skilled_beds = inputToNumber(payload.censusSkilled);
    if (payload.censusAssisted !== undefined) out.assisted_beds = inputToNumber(payload.censusAssisted);
    if (payload.censusIndependent !== undefined) out.independent_beds = inputToNumber(payload.censusIndependent);
    if (payload.censusAddlExposure !== undefined) out.additional_beds = inputToNumber(payload.censusAddlExposure);
    if (payload.licensedSnf !== undefined) out.skilled_beds_licensed = inputToNumber(payload.licensedSnf);
    if (payload.licensedAlf !== undefined) out.assisted_beds_licensed = inputToNumber(payload.licensedAlf);
    if (payload.licensedIlf !== undefined) out.independent_beds_licensed = inputToNumber(payload.licensedIlf);
    if (payload.licensedMc !== undefined) out.memory_care_beds_licensed = inputToNumber(payload.licensedMc);
    // Maturity Credit — FE whole-percent ("100") → backend fraction (1.0).
    if (payload.maturityCredit !== undefined) {
        const pct = inputToNumber(payload.maturityCredit);
        // toFixed strips the float artifact (99.215 / 100 = 0.99215000…1).
        out.maturity_credit = pct == null ? null : Number((pct / 100).toFixed(6));
    }
    // Archive / restore (OR-1431). A real backend column, so an
    // archive-only PATCH produces a non-empty body and clears the
    // `hasPersistableLocationFields` gate.
    if (payload.isExcluded !== undefined) out.is_excluded = payload.isExcluded;
    // Intentionally dropped (no backend column): censusMemoryCare.
    // providerNumber persists as `cms_number` (real column); acquired /
    // operator are still forward-compat (ignored until their migration ships).
    if (computed) {
        out.equivalency = computed.equivalency;
        out.actuarial_rate = computed.actuarial_rate;
        out.unadjusted_premium = computed.unadjusted_premium;
        out.maturity_adjusted_premium = computed.maturity_adjusted_premium;
        out.sir_credit = computed.sir_credit;
        out.sir_adjusted_premium = computed.sir_adjusted_premium;
    }
    return out;
}

/** True when a write payload touches at least one column the backend
 *  location row can store. A payload of ONLY FE-only fields
 *  (censusMemoryCare) maps to an empty body — callers should skip the
 *  PATCH entirely rather than fire a no-op `{}` request. */
export function hasPersistableLocationFields(payload: FacilityWritePayload): boolean {
    return Object.keys(facilityToLocationWrite(payload)).length > 0;
}

export function listFacilities(submissionId: string): Promise<Facility[]> {
    return listLocations(submissionId).then((rows) => rows.map(locationToFacility));
}

export function createFacility(
    submissionId: string,
    payload: FacilityWritePayload,
    computed?: LocationComputedFields,
): Promise<Facility> {
    return createLocation(submissionId, facilityToLocationWrite(payload, computed)).then(locationToFacility);
}

export function patchFacility(
    submissionId: string,
    facilityId: string,
    payload: FacilityWritePayload,
    computed?: LocationComputedFields,
): Promise<Facility> {
    return patchLocation(submissionId, facilityId, facilityToLocationWrite(payload, computed)).then(locationToFacility);
}

export function deleteFacility(submissionId: string, facilityId: string): Promise<void> {
    return deleteLocation(submissionId, facilityId);
}
